import type { MeResponse } from '@process-hub/shared';
import { useEffect, useState, type ReactNode } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { api } from '../api';
import { Footer } from './Footer';

/**
 * Layout from design/Process Platform.dc.html: navy nav, white header, tinted page.
 * Screens not yet built are listed but disabled so the shape of the product is visible.
 */

const NAV: Array<{ label: string; to: string; ready: boolean; adminOnly?: boolean }> = [
  { label: 'Dashboard', to: '/dashboard', ready: true },
  { label: 'Department library', to: '/', ready: true },
  { label: 'Interview', to: '/interview', ready: true },
  { label: 'Approval queue', to: '/approvals', ready: true },
  { label: 'Automation opportunities', to: '/automation', ready: true },
  { label: 'Admin', to: '/admin', ready: true, adminOnly: true },
  { label: 'Help', to: '/help', ready: true },
];

export function Shell({ me, onSignOut, children }: { me: MeResponse; onSignOut: () => void; children: ReactNode }) {
  const { pathname } = useLocation();
  const [awaitingMe, setAwaitingMe] = useState(0);
  useEffect(() => {
    // Badge: decisions waiting on this person. Refreshed on navigation; cheap and anonymous-safe.
    api
      .approvalQueue()
      .then((q) => setAwaitingMe(q.filter((i) => i.canDecide).length))
      .catch(() => setAwaitingMe(0));
  }, [pathname]);
  const title = pathname.startsWith('/processes/')
    ? 'Process detail'
    : pathname.startsWith('/interview/')
      ? 'Interview'
      : (NAV.find((n) => n.to === pathname)?.label ?? 'Process Hub');
  const initials = me.name
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  // Profile photo from Entra when there is one; initials otherwise. Fetched once per sign-in.
  const [photo, setPhoto] = useState<string | null>(null);
  useEffect(() => {
    let url: string | null = null;
    api
      .mePhoto()
      .then((b) => {
        if (b) {
          url = URL.createObjectURL(b);
          setPhoto(url);
        }
      })
      .catch(() => setPhoto(null));
    return () => {
      if (url) URL.revokeObjectURL(url);
    };
  }, [me.oid]);

  return (
    <div className="shell">
      <nav className="nav" aria-label="Primary">
        <div className="nav__brand">
          <img className="nav__logo" src="/brand/weaver-logo-white.svg" alt="Weaver Technologies" width="192" height="59" />
          <strong>Process Hub</strong>
        </div>
        <div className="nav__links">
          {NAV.filter((n) => !n.adminOnly || me.isAdmin).map((n) =>
            n.ready ? (
              <NavLink key={n.to} to={n.to} end={n.to === '/'} className={({ isActive }) => `nav__link${isActive ? ' nav__link--active' : ''}`}>
                <span>{n.label}</span>
                {n.to === '/approvals' && awaitingMe > 0 && <span className="nav__badge" aria-label={`${awaitingMe} awaiting your decision`}>{awaitingMe}</span>}
              </NavLink>
            ) : (
              <button key={n.to} type="button" className="nav__link nav__link--disabled" disabled title="Not built yet">
                <span>{n.label}</span>
              </button>
            ),
          )}
        </div>
        <div className="nav__user">
          {photo ? (
            <img className="nav__avatar" src={photo} alt="" style={{ objectFit: 'cover' }} onError={() => setPhoto(null)} />
          ) : (
            <div className="nav__avatar" aria-hidden="true">
              {initials}
            </div>
          )}
          <div>
            <strong>{me.name}</strong>
            <span>{me.isAdmin ? 'Admin' : me.roles.join(', ') || 'No role'} · Entra ID</span>
          </div>
          <button type="button" className="nav__signout" onClick={onSignOut}>
            Sign out
          </button>
        </div>
      </nav>
      <main className="main">
        <header className="header">
          <div className="header__title">{title}</div>
          <div className="header__crumb">{me.departments.map((d) => d.name).join(' · ') || 'No department'}</div>
        </header>
        <div className="content">{children}</div>
        <Footer />
      </main>
    </div>
  );
}
